import React, { useEffect, useRef, useState } from 'react';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { dataService } from '../../services/dataService';
import { isSuperAdminSession } from '../../redux/helper';
import SessionWarningModal from './SessionWarningModal';
import config from '../../config';

const INACTIVITY_WARNING = 14 * 60 * 1000; // show warning at 14 min
const WARNING_DURATION   = 60 * 1000;       // auto-logout 1 min after warning

const AutoLogout = ({}) => {
    const navigate = useNavigate();
    const ldClient = useLDClient();
    const sblogin     = ldClient.variation('SBLogin', false);
    const SellerLogin = ldClient.variation('SellerLogin', false);
    const email_ID    = localStorage.getItem('user');

    const [showWarning, setShowWarning] = useState(false);

    const inactivityTimerRef = useRef(null);
    const warningTimerRef    = useRef(null);
    const initializedRef     = useRef(false);
    const tokenRef           = useRef(null);
    const warningActiveRef   = useRef(false);

    // ── Logout ────────────────────────────────────────────────────────────────
    const logoutUser = async () => {
        clearTimeout(inactivityTimerRef.current);
        clearTimeout(warningTimerRef.current);
        warningActiveRef.current = false;
        const sellerEmail = sessionStorage.getItem('selectedSellerEmail');
        if (sellerEmail) {
            await dataService.releaseSuperAdminSession(sellerEmail).catch(() => {});
        }
        await Auth.signOut();
        sessionStorage.clear();
        localStorage.clear();
        navigate('/login');
    };

    // ── Inactivity timer (super-admin only) ───────────────────────────────────
    const resetInactivityTimer = () => {
        if (!isSuperAdminSession()) return;
        if (warningActiveRef.current) return; // modal is showing — don't reset
        clearTimeout(inactivityTimerRef.current);
        clearTimeout(warningTimerRef.current);
        inactivityTimerRef.current = setTimeout(() => {
            warningActiveRef.current = true;
            setShowWarning(true);
            warningTimerRef.current = setTimeout(() => {
                logoutUser();
            }, WARNING_DURATION);
        }, INACTIVITY_WARNING);
    };

    const handleStayLoggedIn = () => {
        warningActiveRef.current = false;
        setShowWarning(false);
        resetInactivityTimer();
    };

    useEffect(() => {
        const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

        const startTracking = () => {
            if (!isSuperAdminSession()) return;
            activityEvents.forEach(e => document.addEventListener(e, resetInactivityTimer));
            resetInactivityTimer();
        };

        startTracking(); // works on refresh (selectedSellerEmail already set)
        window.addEventListener('superAdminSessionReady', startTracking); // works on first login

        return () => {
            window.removeEventListener('superAdminSessionReady', startTracking);
            activityEvents.forEach(e => document.removeEventListener(e, resetInactivityTimer));
            clearTimeout(inactivityTimerRef.current);
            clearTimeout(warningTimerRef.current);
        };
    }, []);

    // ── beforeunload — release lock when tab/browser is closed ────────────────
    const refreshToken = async () => {
        try {
            const session = await Auth.currentSession();
            tokenRef.current = session.getIdToken().getJwtToken();
        } catch {
            tokenRef.current = null;
        }
    };

    useEffect(() => {
        // Always register — a fresh impersonation session may start later via
        // the 'superAdminSessionReady' event, well after this component (mounted
        // unconditionally at app load) first renders as a non-super-admin session.
        refreshToken();
        window.addEventListener('superAdminSessionReady', refreshToken);
        const tokenRefreshInterval = setInterval(refreshToken, 10 * 60 * 1000);

        let released = false;
        const releaseOnUnload = () => {
            if (released) return; // beforeunload/pagehide can both fire on the same close
            const email = sessionStorage.getItem('selectedSellerEmail');
            const token = tokenRef.current;
            if (!email || !token) return; // not a super-admin session — do nothing
            released = true;
            fetch(
                `${config.API.baseURL}/users-management/super-admin/session?seller_email=${encodeURIComponent(email)}`,
                { method: 'DELETE', headers: { Authorization: `Bearer ${token}` }, keepalive: true }
            );
            sessionStorage.clear();
            localStorage.clear();
        };

        // 'beforeunload' doesn't fire reliably on all browsers (notably Safari/mobile
        // tab close) — 'pagehide' is the more consistent signal there, so cover both.
        window.addEventListener('beforeunload', releaseOnUnload);
        window.addEventListener('pagehide', releaseOnUnload);
        return () => {
            clearInterval(tokenRefreshInterval);
            window.removeEventListener('superAdminSessionReady', refreshToken);
            window.removeEventListener('beforeunload', releaseOnUnload);
            window.removeEventListener('pagehide', releaseOnUnload);
        };
    }, []);

    // ── Mid-session lock polling (every 2 min) ────────────────────────────────
    useEffect(() => {
        if (!isSuperAdminSession()) return;

        const email       = sessionStorage.getItem('selectedSellerEmail');
        const currentUser = localStorage.getItem('user');

        const pollInterval = setInterval(async () => {
            const result = await dataService.getSuperAdminSession(email);
            if (result.error) return; // ignore transient network errors
            if (!result.data?.locked) { logoutUser(); return; }
            const lockedBy = result.data?.data?.admin_email;
            if (lockedBy !== currentUser) logoutUser();
        }, 2 * 60 * 1000);

        return () => clearInterval(pollInterval);
    }, []);

    // ── LaunchDarkly: global kill-switches ────────────────────────────────────
    useEffect(() => {
        if (sblogin || SellerLogin) logoutUser();
    }, [sblogin, SellerLogin]);

    // ── LaunchDarkly: per-user kill-switch ────────────────────────────────────
    const identifyUserAndCheckFlag = async () => {
        if (!email_ID) return;
        const context = { kind: 'user', key: email_ID, email: email_ID };
        try {
            await ldClient.identify(context);
            await ldClient.waitForInitialization();
            if (ldClient.variation('SellerSpecificUser', false)) {
                sessionStorage.setItem('log', 'true');
                logoutUser();
            }
        } catch (error) {
            console.log('Error identifying user or checking flag:', error);
        }
    };

    const setupFlagChangeListener = () => {
        if (initializedRef.current) return;
        ldClient.on('change', (changes) => {
            if (changes.SellerSpecificUser?.current) {
                sessionStorage.setItem('log', 'true');
                logoutUser();
            }
        });
        initializedRef.current = true;
    };

    useEffect(() => {
        if (ldClient && email_ID) {
            identifyUserAndCheckFlag();
            setupFlagChangeListener();
        }
    }, [ldClient, email_ID]);

    return (
        <SessionWarningModal
            isOpen={showWarning}
            onStayLoggedIn={handleStayLoggedIn}
            onLogout={logoutUser}
        />
    );
};

export default AutoLogout;